import React, { useState, useEffect } from 'react';
import { useAdminAuth } from '../../hooks/useAdminAuth';

function AdminLayout({ children, activeSection, onSectionChange, onLogout }) {
  const { admin, logout, isAdmin } = useAdminAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      if (window.innerWidth >= 768) {
        setSidebarOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
      if (onLogout) {
        onLogout();
      }
    } catch (error) {
      console.error('Error logging out:', error);
      if (onLogout) {
        onLogout();
      }
    }
  };

  const handleSectionChange = (sectionId) => {
    onSectionChange(sectionId);
    if (isMobile) {
      setSidebarOpen(false);
    }
  };

  const sections = [
    { id: 'dashboard', label: 'Dashboard', icon: 'bi-speedometer2' },
    { id: 'users', label: 'Users', icon: 'bi-people' },
    { id: 'deposits', label: 'Deposits', icon: 'bi-wallet2' },
    { id: 'withdrawals', label: 'Withdrawals', icon: 'bi-arrow-down-circle' },
    { id: 'withdrawal-limits', label: 'Withdrawal Limits', icon: 'bi-currency-dollar' },
    { id: 'manual-knife', label: 'Manual Knife', icon: 'bi-dagger' },
    { id: 'whatsapp', label: 'WhatsApp', icon: 'bi-whatsapp' },
    { id: 'unlocks', label: 'Unlock Requests', icon: 'bi-unlock' },
    { id: 'hero-text', label: 'Hero Text', icon: 'bi-textarea-t' },
    { id: 'admin-users', label: 'Admin Users', icon: 'bi-shield-check' },
    { id: 'activity', label: 'Activity Log', icon: 'bi-clock-history' }
  ];

  return (
    <div className="admin-layout" style={{ minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
      {/* Header - Mobile Responsive */}
      <nav className="navbar navbar-dark bg-primary" style={{ 
        padding: isMobile ? '0.75rem 1rem' : '1rem 2rem'
      }}>
        <div className="container-fluid">
          <div className="d-flex align-items-center">
            {/* Mobile Menu Toggle */}
            <button
              className="btn btn-link text-white d-md-none me-2 p-2"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              style={{ 
                minWidth: '44px',
                minHeight: '44px',
                fontSize: '1.25rem'
              }}
              aria-label="Toggle menu"
            >
              <i className={`bi ${sidebarOpen ? 'bi-x-lg' : 'bi-list'}`}></i>
            </button>
            <span className="navbar-brand mb-0" style={{ fontSize: isMobile ? '1.1rem' : '1.5rem' }}>
              <i className="bi bi-shield-check me-2"></i>
              <span className="d-none d-sm-inline">Admin Panel</span>
              <span className="d-sm-none">Admin</span>
            </span>
          </div>
          <div className="d-flex align-items-center">
            <span className="text-white me-2 d-none d-md-inline" style={{ fontSize: '0.9rem' }}>
              <i className="bi bi-person-circle me-1"></i>
              {admin?.email || 'Admin'}
            </span>
            <button 
              className="btn btn-outline-light"
              onClick={handleLogout}
              style={{ 
                minWidth: isMobile ? '44px' : 'auto',
                minHeight: '44px',
                fontSize: isMobile ? '0.9rem' : '0.875rem',
                padding: isMobile ? '0.5rem' : '0.375rem 0.75rem'
              }}
            >
              <i className="bi bi-box-arrow-right me-md-2"></i>
              <span className="d-none d-md-inline">Logout</span>
            </button>
          </div>
        </div>
      </nav>

      <div className="d-flex position-relative" style={{ minHeight: 'calc(100vh - 56px)' }}>
        {/* Mobile Overlay */}
        {isMobile && sidebarOpen && (
          <div
            className="position-fixed"
            style={{
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 1040
            }}
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Sidebar - Responsive */}
        <div
          className={`bg-white border-end position-md-static ${
            isMobile ? 'position-fixed' : ''
          }`}
          style={{
            width: isMobile ? '280px' : '250px',
            padding: isMobile ? '1rem 0.75rem' : '1rem',
            zIndex: isMobile ? 1050 : 'auto',
            height: isMobile ? '100%' : 'auto',
            transform: isMobile && !sidebarOpen ? 'translateX(-100%)' : 'translateX(0)',
            transition: 'transform 0.3s ease-in-out',
            boxShadow: isMobile ? '2px 0 10px rgba(0,0,0,0.1)' : 'none'
          }}
        >
          <nav className="nav flex-column">
            {sections.map((section) => (
              <button
                key={section.id}
                className={`btn text-start mb-2 w-100 ${
                  activeSection === section.id ? 'btn-primary' : 'btn-outline-secondary'
                }`}
                onClick={() => handleSectionChange(section.id)}
                style={{ 
                  textAlign: 'left',
                  minHeight: '44px',
                  padding: '0.75rem 1rem',
                  fontSize: '0.95rem',
                  touchAction: 'manipulation'
                }}
              >
                <i className={`bi ${section.icon} me-2`}></i>
                {section.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Main Content - Responsive Padding */}
        <div 
          className="flex-grow-1" 
          style={{ 
            padding: isMobile ? '1rem' : '2rem',
            width: '100%',
            overflowX: 'auto'
          }}
        >
          {children}
        </div>
      </div>
    </div>
  );
}

export default AdminLayout;

